import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import { ATTACHMENTS_BUCKET } from "../commons/attachments";
import {
  type Branding,
  isSameBranding,
  omitBranding,
  pickBranding,
} from "../commons/configurationScopes";
import { getCurrentTeamId } from "./currentTeam";
import { extractBucketPath } from "./signedStorageUrls";
import { getSupabaseClient } from "./supabase";

const GLOBAL_CONFIGURATION_ID = 1;

// A logo read back is a signed URL: compare files by their path in the bucket
const normalizeBranding = (branding: Branding): Branding =>
  Object.fromEntries(
    Object.entries(branding).map(([key, value]) => [
      key,
      typeof value === "string"
        ? (extractBucketPath(value, ATTACHMENTS_BUCKET) ?? value)
        : value,
    ]),
  ) as Branding;

const readGlobal = async () => {
  const { data, error } = await getSupabaseClient()
    .from("configuration")
    .select("config")
    .eq("id", GLOBAL_CONFIGURATION_ID)
    .maybeSingle();
  if (error) throw error;
  return (data?.config ?? {}) as Record<string, unknown>;
};

/** The team's settings with the platform branding (RLS returns the caller's team row). */
export const readConfiguration =
  async (): Promise<ConfigurationContextValue> => {
    const [global, { data: team, error }] = await Promise.all([
      readGlobal(),
      getSupabaseClient()
        .from("team_configuration")
        .select("config")
        .maybeSingle(),
    ]);
    if (error) throw error;
    return {
      ...omitBranding((team?.config ?? {}) as Record<string, unknown>),
      ...pickBranding(global),
    } as ConfigurationContextValue;
  };

/**
 * Saves the team settings for the caller's team (team admins only, RLS) and,
 * when it changed, the branding (sales.can_manage_configuration only).
 */
export const writeConfiguration = async (
  config: ConfigurationContextValue,
): Promise<ConfigurationContextValue> => {
  const teamSettings = omitBranding(
    config as unknown as Record<string, unknown>,
  );
  const { error: teamError } = await getSupabaseClient()
    .from("team_configuration")
    .upsert({
      team_id: await getCurrentTeamId(),
      config: teamSettings,
      updated_at: new Date().toISOString(),
    });
  if (teamError) throw teamError;

  const global = await readGlobal();
  // Team admins submit the branding they were shown (defaults included):
  // only the platform owner saves it
  const { data: canManage } = await getSupabaseClient().rpc(
    "can_manage_configuration",
  );
  if (!canManage) {
    return {
      ...teamSettings,
      ...pickBranding(global),
    } as ConfigurationContextValue;
  }
  const branding = pickBranding(config as unknown as Record<string, unknown>);
  if (
    !isSameBranding(
      normalizeBranding(pickBranding(global)),
      normalizeBranding(branding),
    )
  ) {
    const { data, error } = await getSupabaseClient()
      .from("configuration")
      .update({ config: { ...global, ...branding } })
      .eq("id", GLOBAL_CONFIGURATION_ID)
      .select("id");
    if (error) throw error;
    if (!data?.length) {
      throw new Error("Only the platform owner can change the name and logos");
    }
  }
  return { ...teamSettings, ...branding } as ConfigurationContextValue;
};
